import React, { useEffect, useRef, useState } from "react";
import { Col } from "reactstrap";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import LocationOnSharpIcon from "@mui/icons-material/LocationOnSharp";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import GroupsSharpIcon from "@mui/icons-material/GroupsSharp";
import BusinessSharpIcon from "@mui/icons-material/BusinessSharp";
import WorkOutlineSharpIcon from "@mui/icons-material/WorkOutlineSharp";
import DynamicFeedOutlinedIcon from "@mui/icons-material/DynamicFeedOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SearchSharpIcon from "@mui/icons-material/SearchSharp";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PersonAddAlt1SharpIcon from "@mui/icons-material/PersonAddAlt1Sharp";
import PersonRemoveAlt1SharpIcon from "@mui/icons-material/PersonRemoveAlt1Sharp";
import CheckSharpIcon from "@mui/icons-material/CheckSharp";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkOutlinedIcon from "@mui/icons-material/BookmarkOutlined";
import TagIcon from "@mui/icons-material/Tag";
import PublicSharpIcon from "@mui/icons-material/PublicSharp";
import MoreHorizSharpIcon from "@mui/icons-material/MoreHorizSharp";
import ReplyAllOutlinedIcon from "@mui/icons-material/ReplyAllOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import axios from "../../API/axios";
import { recordSearch } from "../../API/searchHistory";
import useNewsFeedStore from "../NewsFeed/useNewsFeedStore";
import {
  ListRowSkeleton,
  GridCardSkeleton,
  JobCardSkeleton,
  BlogGridSkeleton,
  PostFeedSkeleton,
} from "@skeleton";
import { post, people } from "../../config/imagesLink";
import { extractPostInfoFromGetPostResponse } from "../../utility/newsFeedPost";
import { mapGlobalSearchList } from "../../utility/globalSearchMap";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";
import "./SearchResult.css";

const getTabFromRoute = (routeTab) => {
  const tab = String(routeTab || "").toLowerCase();

  switch (tab) {
    case "people":
      return 1;
    case "companies":
      return 2;
    case "jobs":
      return 3;
    case "posts":
      return 4;
    case "hashtags":
      return 5;
    case "articles":
      return 6;
    case "blogs":
      return 7;
    default:
      return 1;
  }
};

const SEARCH_TAB_ROUTES = {
  1: "People",
  2: "Companies",
  3: "Jobs",
  4: "Posts",
  5: "Hashtags",
  6: "Articles",
  7: "Blogs",
};

const SEARCH_TABS = [
  { id: 1, labelKey: "Sidemenubar.People", icon: GroupsSharpIcon },
  { id: 2, labelKey: "Sidemenubar.Companies", icon: BusinessSharpIcon },
  { id: 3, labelKey: "Company Profile.Jobs", icon: WorkOutlineSharpIcon },
  { id: 4, labelKey: "Company Profile.Posts", icon: DynamicFeedOutlinedIcon },
  { id: 5, labelKey: "Newsfeed.Hashtag", icon: TagIcon },
  { id: 6, labelKey: "Newsfeed.Article", icon: ArticleOutlinedIcon },
  { id: 7, labelKey: "Sidemenubar.Blogs", icon: MenuBookOutlinedIcon },
];

const parseTitleAndHandle = (title) => {
  const raw = String(title || "").trim();
  const match = raw.match(/^(.*?)\s*\((@[^)]+)\)\s*$/);
  if (match) {
    return { name: match[1].trim(), handle: match[2].trim() };
  }
  return { name: raw, handle: "" };
};

const truncateText = (value, max = 160) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}...`;
};

const EMPTY_API_META = {
  company: "",
  location: "",
  salary: "",
  posted: "",
  jobType: "",
  workType: "",
  headline: "",
  summary: "",
  followers: "",
  mutual: "",
  degree: "",
  author: "",
  title: "",
  content: "",
  date: "",
  likes: 0,
  comments: 0,
  reposts: 0,
  description: "",
  authorTitle: "",
};

// Missing API fields stay empty; search results never invent portal data.
const pickDummy = () => EMPTY_API_META;
const markDummy = (value) => String(value || "").trim();

const looksLikeDateOnly = (value) => {
  const text = String(value || "").trim();
  if (!text) return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return true;
  const parsed = moment(text, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY"], true);
  return parsed.isValid() && text.length < 32;
};

const stripHtmlToText = (value) => {
  if (value == null) return "";
  const raw = String(value);
  if (typeof DOMParser !== "undefined") {
    return (
      new DOMParser().parseFromString(raw, "text/html").body.textContent || ""
    )
      .replace(/\s+/g, " ")
      .trim();
  }
  return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const SearchResult = ({ searchQuery = "" }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab: routeTab } = useParams();
  const { setIsNewsPageLoaded } = useNewsFeedStore();
  const searchQueryFromUrl = new URLSearchParams(location.search).get("q") || "";
  const searchQueryFromState = location.state?.searchQuery || "";
  const resolvedSearchQuery = searchQuery || searchQueryFromUrl || searchQueryFromState;

  const [activeTab, setActiveTab] = useState(() => getTabFromRoute(routeTab));
  const [isLoading, setIsLoading] = useState(true);
  const [nextBatchLoading, setNextBatchLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [inputValue, setInputValue] = useState(resolvedSearchQuery);
  const [debouncedInputValue, setDebouncedInputValue] = useState(
    resolvedSearchQuery.trim()
  );
  const [hasTabChanged, setHasTabChanged] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [options, setOptions] = useState([]);
  const [searchUser, setSearchUser] = useState([]);
  const [searchHashtag, setSearchHashtag] = useState([]);
  const [searchComp, setSearchComp] = useState([]);
  const [searchJob, setSearchJob] = useState([]);
  const [searchPost, setSearchPost] = useState([]);
  const [searchBlog, setSearchBlog] = useState([]);
  const [searchArticle, setSearchArticle] = useState([]);
  const [followedMap, setFollowedMap] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [appliedMap, setAppliedMap] = useState({});
  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [unfollowTarget, setUnfollowTarget] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const isFetching = useRef(false);
  const debounceRef = useRef(null);
  const sessionId = localStorage.getItem("sessionId");
  const guestModeData = localStorage.getItem("guestMode");
  const language = localStorage.getItem("Lang");

  useEffect(() => {
    setIsNewsPageLoaded(true);
    return () => setIsNewsPageLoaded(false);
  }, [setIsNewsPageLoaded]);

  useEffect(() => {
    const nextSearchValue = resolvedSearchQuery.trim();

    if (nextSearchValue !== inputValue) {
      setInputValue(nextSearchValue);
    }
  }, [resolvedSearchQuery]);

  useEffect(() => {
    const trimmedValue = inputValue.trim();
    const params = new URLSearchParams(location.search);

    if (trimmedValue) {
      params.set("q", trimmedValue);
    } else {
      params.delete("q");
    }

    const nextSearch = params.toString();
    const normalizedNextSearch = nextSearch ? `?${nextSearch}` : "";

    if (normalizedNextSearch === location.search) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: normalizedNextSearch,
      },
      {
        replace: true,
        state: {
          ...(location.state || {}),
          searchQuery: trimmedValue,
        },
      }
    );
  }, [inputValue, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedInputValue(inputValue.trim());
    }, 1000);

    return () => clearTimeout(delayDebounce);
  }, [inputValue]);

  const clearResults = () => {
    setSearchUser([]);
    setSearchHashtag([]);
    setSearchComp([]);
    setSearchJob([]);
    setSearchPost([]);
    setSearchBlog([]);
    setSearchArticle([]);
    setOptions([]);
  };

  const enrichPostsFromApi = async (posts = []) => {
    if (!posts.length) return [];

    const token = localStorage.getItem("restrictedViewerToken") || "";
    const langKey = language === "en" ? "" : language || "";

    const enrichedList = await Promise.all(
      posts.map(async (item) => {
        if (!item?.id || item?.enriched) return item;

        try {
          const res = await axios.get(
            `/Posts/GetPostInfo?DeviceType=Web&PostId=${item.id}&RestrictedViewerToken=${token}&langKey=${langKey}`
          );
          const postInfo = extractPostInfoFromGetPostResponse(
            res?.data?.responseData
          );
          if (!postInfo) return item;

          const contentText = stripHtmlToText(
            postInfo.postContent ||
              postInfo.description ||
              postInfo.shortDescription ||
              ""
          );

          return {
            ...item,
            enriched: true,
            image: postInfo.userAvatar || item.image,
            userUid: postInfo.userUid || item.userUid,
            author: postInfo.userName || item.author,
            nameTag: postInfo.nameTag || item.nameTag,
            headline:
              postInfo.title ||
              postInfo.additionalInfo ||
              item.headline ||
              "",
            content: contentText,
            creationTime: postInfo.creationTime || item.creationTime,
            likeCount: postInfo.likeCount ?? item.likeCount,
            commentCount: postInfo.commentCount ?? item.commentCount,
            shareCount: postInfo.shareCount ?? item.shareCount,
            repostCount:
              postInfo.repostCount ??
              postInfo.shareCount ??
              item.repostCount,
            slug: postInfo.slug || item.slug,
          };
        } catch (error) {
          return item;
        }
      })
    );

    return enrichedList;
  };

  const addOptions = () => {
    if (isFetching.current) return;

    isFetching.current = true;
    setIsLoading(true);
    const searchValue = debouncedInputValue || "All";
    const data = {
      deviceType: "Web",
      pageNumber: page,
      pageSize: 10,
      langKey: language === "en" ? "" : language || "",
      search: searchValue,
      type:
        activeTab === 1
          ? "User"
          : activeTab === 2
            ? "Company"
            : activeTab === 3
              ? "Job"
              : activeTab === 4
                ? "Post"
                : activeTab === 5
                  ? "Hashtag"
                  : activeTab === 6
                    ? "Article"
                    : activeTab === 7
                      ? "Blog"
                      : "",
    };

    if (page === 1 && searchValue && searchValue !== "All") {
      recordSearch({ searchKey: searchValue, typeId: 1 });
    }

    axios
      .post("SMFeatures/GlobalSearchWithFilter", data)
      .then(async (response) => {
        if (
          !response.data ||
          !response.data.responseData ||
          !response.data.responseData.searchdata
        ) {
          setIsError(true);
          setIsLoading(false);
          setNextBatchLoading(false);
          setHasMoreData(false);
          clearResults();
          return;
        }

        let data = response.data.responseData.searchdata;

        if (Array.isArray(data) && data.length === 0) {
          setIsError(true);
          setIsLoading(false);
          setNextBatchLoading(false);
          setHasMoreData(false);
          clearResults();
          return;
        }

        setIsError(false);
        // Flatten nested peopleInfo/companyInfo (isFollowedByYou, followers, etc.)
        data = mapGlobalSearchList(data);

        const addUniqueItems = (prevList, nextList) => {
          if (page === 1) {
            return nextList;
          }

          const itemSet = new Set(prevList.map((item) => item.id));
          const uniqueNewItems = nextList.filter((item) => !itemSet.has(item.id));
          return [...prevList, ...uniqueNewItems];
        };

        const users = data.filter((item) => item.type === "User");
        const hashtags = data.filter((item) => item.type === "Hashtag");
        const companies = data.filter((item) => item.type === "Company");
        const jobs = data.filter((item) => item.type === "Job");
        let posts = data.filter((item) => item.type === "Post");
        const blogs = data.filter((item) => item.type === "Blog");
        const articles = data.filter((item) => item.type === "Article");

        setSearchUser((prev) => addUniqueItems(prev, users));
        setSearchHashtag((prev) => addUniqueItems(prev, hashtags));
        setSearchComp((prev) => addUniqueItems(prev, companies));
        setSearchJob((prev) => addUniqueItems(prev, jobs));
        setSearchPost((prev) => addUniqueItems(prev, posts));
        setSearchBlog((prev) => addUniqueItems(prev, blogs));
        setSearchArticle((prev) => addUniqueItems(prev, articles));

        setIsLoading(false);
        setNextBatchLoading(false);
        const nextTotalPages = response.data.responseData.paging?.totalPages || 1;
        setTotalPages(nextTotalPages);
        setHasMoreData(page < nextTotalPages);

        setOptions((prev) => {
          const postSet = new Set(prev.map((postItem) => postItem.id));
          const uniqueNewPosts = data.filter((postItem) => !postSet.has(postItem.id));
          return page === 1 ? uniqueNewPosts : [...prev, ...uniqueNewPosts];
        });

        if (posts.length > 0) {
          const enrichedPosts = await enrichPostsFromApi(posts);
          setSearchPost((prev) => {
            const byId = new Map(
              enrichedPosts.map((postItem) => [String(postItem.id), postItem])
            );
            return prev.map((postItem) => byId.get(String(postItem.id)) || postItem);
          });
        }
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
        setNextBatchLoading(false);
        setHasMoreData(false);
        clearResults();
      })
      .finally(() => {
        isFetching.current = false;
      });
  };

  useEffect(() => {
    clearResults();
    setIsError(false);
    setTotalPages(1);
    setHasMoreData(true);

    if (debouncedInputValue && debouncedInputValue.length < 3) {
      setIsLoading(false);
      setNextBatchLoading(false);
      setHasMoreData(false);
      return;
    }

    if (page !== 1) {
      setPage(1);
      return;
    }

    addOptions();
  }, [debouncedInputValue, activeTab]);

  useEffect(() => {
    if (page > 1) {
      setNextBatchLoading(true);
      addOptions();
    }
  }, [page]);

  useEffect(() => {
    const nextTab = getTabFromRoute(routeTab);

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [activeTab, routeTab]);

  const goToSearchTab = (tabIndex) => {
    setHasTabChanged(true);
    setPage(1);
    setActiveTab(tabIndex);
    const tabPath = SEARCH_TAB_ROUTES[tabIndex];
    if (tabPath) {
      navigate({
        pathname: `/SearchResult/${tabPath}`,
        search: location.search,
      });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const handleInfiniteScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 10 >=
          document.documentElement.scrollHeight &&
        !isLoading &&
        hasMoreData &&
        !isFetching.current &&
        page < totalPages
      ) {
        if (debounceRef.current) return;

        debounceRef.current = setTimeout(() => {
          setPage((prev) => prev + 1);
          debounceRef.current = null;
        }, 300);
      }
    };

    window.addEventListener("scroll", handleInfiniteScroll);

    return () => {
      window.removeEventListener("scroll", handleInfiniteScroll);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [hasMoreData, isLoading, page, totalPages]);

  const renderEmptyState = () => {
    if (isLoading && page === 1) {
      if (activeTab === 1 || activeTab === 2) {
        return <GridCardSkeleton className="mt-3" count={4} cardHeight={160} />;
      }
      if (activeTab === 3) {
        return <JobCardSkeleton className="mt-3" count={4} />;
      }
      if (activeTab === 4 || activeTab === 6) {
        return <PostFeedSkeleton className="mt-3" count={3} />;
      }
      if (activeTab === 7) {
        return <BlogGridSkeleton className="mt-3" count={4} />;
      }
      return (
        <ListRowSkeleton className="mt-3" count={5} rowHeight={80} showAvatar />
      );
    }

    if (isError) {
      return (
        <div className="mt-2 error-image-cont d-flex align-items-center justify-content-center">
          <img src={post.errorSmall} alt="404 Not Found" style={{ width: "250px" }} />
        </div>
      );
    }

    return null;
  };

  const normalizeSearchTitle = (value) => {
    if (value === null || value === undefined) return "";

    const rawValue = String(value);
    let parsedValue = null;

    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      parsedValue = null;
    }

    if (Array.isArray(parsedValue)) {
      const tags = parsedValue
        .map((item) => item?.displayName)
        .filter(Boolean)
        .map((name) => `#${name}`);

      if (tags.length > 0) {
        return [...new Set(tags)].join(" ");
      }
    }

    let cleanedText = rawValue.replace(/\{\d*\}/g, " ");

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      cleanedText =
        parser.parseFromString(cleanedText, "text/html").body.textContent ||
        cleanedText;
    } else {
      cleanedText = cleanedText.replace(/<[^>]*>/g, " ");
    }

    cleanedText = cleanedText.replace(/\s+/g, " ").trim();

    if (!cleanedText) {
      const hashtags = [...rawValue.matchAll(/\/hashtag\/([^"'/?#\s>]+)/gi)].map(
        (match) => {
          try {
            return `#${decodeURIComponent(match[1])}`;
          } catch (error) {
            return `#${match[1]}`;
          }
        }
      );

      if (hashtags.length > 0) {
        return [...new Set(hashtags)].join(" ");
      }
    }

    return cleanedText;
  };

  const normalizeSearchMeta = (value) => {
    if (value === null || value === undefined) return "";

    const rawValue = String(value).trim();
    if (!rawValue) return "";

    let parsedValue = null;
    try {
      parsedValue = JSON.parse(rawValue);
    } catch (error) {
      parsedValue = null;
    }

    if (Array.isArray(parsedValue)) {
      const tags = parsedValue
        .map((item) => item?.displayName)
        .filter(Boolean)
        .map((name) => `#${name}`);

      return [...new Set(tags)].join(" ");
    }

    if (parsedValue && typeof parsedValue === "object") {
      return "";
    }

    let cleanedText = rawValue.replace(/\{\d*\}/g, " ");

    if (typeof DOMParser !== "undefined") {
      const parser = new DOMParser();
      cleanedText =
        parser.parseFromString(cleanedText, "text/html").body.textContent ||
        cleanedText;
    } else {
      cleanedText = cleanedText.replace(/<[^>]*>/g, " ");
    }

    return cleanedText.replace(/\s+/g, " ").trim();
  };

  const formatPostSearchItem = (item) => {
    const title = normalizeSearchTitle(item?.title);
    const additionalInfo = normalizeSearchMeta(item?.additionalInfo);
    const description = normalizeSearchMeta(item?.description);

    return {
      title,
      additionalInfo:
        additionalInfo && additionalInfo !== title ? additionalInfo : "",
      description:
        description && description !== title && description !== additionalInfo
          ? description
          : "",
    };
  };

  const getPostSearchLink = (item) => {
    const normalizedTitle = normalizeSearchTitle(item?.title || "");
    const isHashtagType = item?.type === "Hashtag";
    const looksLikeHashtag = normalizedTitle.startsWith("#") && !!item?.slug;

    if ((isHashtagType || looksLikeHashtag) && item?.slug) {
      return `/hashtag/${encodeURIComponent(item.slug)}`;
    }

    return `/Post/${item?.id}/${item?.slug || ""}`;
  };

  const formatBlogDate = (value) => {
    if (!value) return "";
    const parsed = moment(value);
    if (parsed.isValid()) {
      return parsed.format("dddd, MMMM D, YYYY");
    }
    return normalizeSearchMeta(value);
  };

  const splitJobMeta = (description) => {
    const text = normalizeSearchMeta(description);
    if (!text) return { location: "", salary: "", extra: "" };

    const parts = text
      .split(/\s*[·|•]\s*|\n+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length === 1) {
      const looksLikeSalary = /\$|€|£|₹|\/yr|\/year|\/mo|salary|k\/yr/i.test(
        parts[0]
      );
      return looksLikeSalary
        ? { location: "", salary: parts[0], extra: "" }
        : { location: parts[0], salary: "", extra: "" };
    }

    return {
      location: parts[0] || "",
      salary: parts[1] || "",
      extra: parts.slice(2).join(" · "),
    };
  };

  const handleImageError = (event) => {
    event.target.onerror = null;
    event.target.src = people.DefaultProfile;
  };

  const getFollowState = (type, entityId, apiFollowed = false) => {
    const key = `${type}-${entityId}`;
    const currentlyFollowed = Object.prototype.hasOwnProperty.call(
      followedMap,
      key
    )
      ? Boolean(followedMap[key])
      : Boolean(apiFollowed);
    return { key, currentlyFollowed };
  };

  const showApiErrorToast = (error) => {
    const message = error?.response?.data?.message;
    if (error?.response?.status === 404) {
      setWarningToastMessage(message);
      setShowWarningToast(true);
    } else {
      setErrorToastMessage(message);
      setShowErrorToast(true);
    }
  };

  const closeUnfollowModal = () => {
    setShowUnfollowModal(false);
    setUnfollowTarget(null);
  };

  const handleFollowClick = async (
    event,
    type,
    entityId,
    apiFollowed = false,
    displayName = ""
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (!entityId || guestModeData === "true") return;

    const { key, currentlyFollowed } = getFollowState(
      type,
      entityId,
      apiFollowed
    );

    if (currentlyFollowed) {
      setUnfollowTarget({
        type,
        id: entityId,
        name: displayName,
        key,
      });
      setShowUnfollowModal(true);
      return;
    }

    setFollowedMap((prev) => ({ ...prev, [key]: true }));

    try {
      const res =
        type === "Company"
          ? await axios.post("Entity/Follow/Company", {
              deviceType: "Web",
              sessionId,
              uiD_ID: entityId,
              langKey: language === "en" ? "" : language,
            })
          : await axios.post("UserFollow/FollowUser", {
              deviceType: "Web",
              sessionId,
              userUID: entityId,
              langKey: language === "en" ? "" : language,
            });
      setToastMessage(res?.data?.message);
      setShowToast(true);
    } catch (error) {
      setFollowedMap((prev) => ({ ...prev, [key]: false }));
      showApiErrorToast(error);
    }
  };

  const confirmUnfollow = async () => {
    if (!unfollowTarget?.id) return;

    const { type, id: entityId, key } = unfollowTarget;
    closeUnfollowModal();
    setFollowedMap((prev) => ({ ...prev, [key]: false }));

    try {
      const res =
        type === "Company"
          ? await axios.post("Entity/Unfollow/Company", {
              deviceType: "Web",
              sessionId,
              uiD_ID: entityId,
              langKey: language === "en" ? "" : language,
            })
          : await axios.post("UserFollow/UnfollowUser", {
              deviceType: "Web",
              sessionId,
              userUID: entityId,
              langKey: language === "en" ? "" : language,
            });
      setToastMessage(res?.data?.message);
      setShowToast(true);
    } catch (error) {
      setFollowedMap((prev) => ({ ...prev, [key]: true }));
      showApiErrorToast(error);
    }
  };

  useEffect(() => {
    if (showUnfollowModal) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.body.classList.add("modal-open");
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "";
      document.body.classList.remove("modal-open");
    }
  }, [showUnfollowModal]);

  const handleSaveClick = async (event, item, type) => {
    event.preventDefault();
    event.stopPropagation();
    if (guestModeData === "true") return;

    const id = type === "Job" ? item?.jobId || item?.id : item?.id;
    if (!id) return;
    const key = `${type}-${id}`;
    const apiSaved = type === "Job" ? item?.isSaved : item?.isSavedByYou;
    const current = Object.prototype.hasOwnProperty.call(savedMap, key)
      ? savedMap[key]
      : Boolean(apiSaved);
    setSavedMap((prev) => ({ ...prev, [key]: !current }));

    try {
      const endpoint =
        type === "Job"
          ? current
            ? "JobAction/UnSaveJob"
            : "JobAction/SaveJob"
          : current
            ? "PostAction/UnsavePost"
            : "PostAction/SavePost";
      await axios.post(endpoint, {
        deviceType: "Web",
        sessionId,
        ...(type === "Job" ? { jobId: id } : { postId: id }),
        langKey: language === "en" ? "" : language,
      });
    } catch (error) {
      setSavedMap((prev) => ({ ...prev, [key]: current }));
    }
  };

  const handleApplyClick = async (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    if (guestModeData === "true") return;

    const jobId = item?.jobId || item?.id;
    if (!jobId || item?.isExpired || item?.isClosed || item?.isOwned) return;
    const key = `Job-${jobId}`;
    const current = Object.prototype.hasOwnProperty.call(appliedMap, key)
      ? appliedMap[key]
      : Boolean(item?.isApplied);
    setAppliedMap((prev) => ({ ...prev, [key]: !current }));

    try {
      await axios.post(
        current ? "/JobAction/UnApplyJob" : "/JobAction/ApplyJob",
        {
          deviceType: "Web",
          sessionId,
          jobId,
          type: 1,
          userUid: localStorage.getItem("userUid"),
          langKey: language === "en" ? "" : language,
        }
      );
    } catch (error) {
      setAppliedMap((prev) => ({ ...prev, [key]: current }));
    }
  };

  const renderPeopleOrCompanyCard = (item, type, index) => {
    const displayTitle = normalizeSearchTitle(item?.title);
    const { name, handle } = parseTitleAndHandle(displayTitle);
    const dummy = pickDummy();

    const apiHeadline = normalizeSearchMeta(
      item?.headline || item?.additionalInfo
    );
    const apiLocation = normalizeSearchMeta(
      item?.location || item?.description
    );
    const hasApiFollowers = item?.followers > 0;
    const apiSummary = normalizeSearchMeta(
      item?.summary || item?.shortDescription
    );
    const hasMutual =
      type === "User" && Number(item?.mutualConnectionsCount) > 0;

    const headlineIsDummy = !apiHeadline;
    const locationIsDummy = !apiLocation;
    const followersIsDummy = !hasApiFollowers;
    const summaryIsDummy = !apiSummary;
    const mutualIsDummy = !hasMutual;

    const headline = apiHeadline || dummy.headline;
    const locationText = apiLocation || dummy.location;
    const followersLabel = hasApiFollowers
      ? `${item.followers}`
      : dummy.followers;
    const summary = apiSummary || dummy.summary;
    const mutual = hasMutual
      ? `${item.mutualConnectionsCount} mutual connection${
          item.mutualConnectionsCount === 1 ? "" : "s"
        }`
      : dummy.mutual;
    const degree = type === "User" ? dummy.degree : "";
    const followKey = `${type}-${item?.id}`;
    const isFollowed = Object.prototype.hasOwnProperty.call(
      followedMap,
      followKey
    )
      ? Boolean(followedMap[followKey])
      : Boolean(item?.isFollowedByYou);
    const itemLink =
      type === "Company"
        ? `/Company/${item?.id}/${item?.slug || ""}`
        : `/User/${item?.id}`;
    const messageLink =
      type === "User" ? `/Message/${item?.id}` : null;

    return (
      <div
        key={`${type}-${item?.id || index}`}
        className={`PeopleCard HuzPeopleCard sr-entity-card${
          type === "Company" ? " sr-company-card" : " sr-people-result"
        }`}
      >
        <div className="PeopleCardHeader">
          <img
            src={item?.backGroundImage || item?.coverImage || people.background}
            alt=""
            width="100%"
            height="112"
            onError={(event) => {
              event.currentTarget.src = people.background;
            }}
          />
          <div className="overlay" />
          <div className="centerAlignment">
            <Link to={itemLink} className="sr-people-avatar-link">
              <div className="ProfileImageWrapper">
                <img
                  src={item?.image || people.DefaultProfile}
                  alt=""
                  width={60}
                  height={60}
                  onError={handleImageError}
                />
              </div>
            </Link>
            <div className="d-block">
              <div className="mainCardText mb-0">
                <Link to={itemLink}>
                  <h1>{name || displayTitle}</h1>
                </Link>
              </div>
              {handle ? (
                <div className="mainCardText">
                  <h1 className="gradient-heading f-12">
                    <span>{handle}</span>
                  </h1>
                </div>
              ) : null}
              {type === "User" && degree ? (
                <span className="sr-degree">· {markDummy(degree)}</span>
              ) : null}
              {headline ? (
                <div className="cardSubText">
                  <h1>
                    {headlineIsDummy ? markDummy(headline) : headline}
                  </h1>
                </div>
              ) : null}
              {locationText ? (
                <p className="sr-people-location">
                  <LocationOnSharpIcon className="ConnectionIcons" />
                  <span>
                    {locationIsDummy ? markDummy(locationText) : locationText}
                  </span>
                </p>
              ) : null}
              {summary ? (
                <p className="sr-people-summary">
                  {summaryIsDummy
                    ? markDummy(
                        `${t("User Profile.Summary")}: ${truncateText(
                          summary
                            .replace(/^Summary:\s*/i, "")
                            .replace(/^About:\s*/i, ""),
                          90
                        )}`
                      )
                    : truncateText(summary, 100)}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="PeopleCardContainer p-2 d-flex align-items-center justify-content-between">
          {followersLabel || mutual ? (
            <div className="cardSubText d-flex align-items-center gap-1">
              {type === "Company" ? (
                <BusinessSharpIcon sx={{ fontSize: 16, color: "#00419f" }} />
              ) : (
                <GroupsOutlinedIcon className="ConnectionIcons" />
              )}
              {followersLabel ? (
                <h1>
                  {followersIsDummy
                    ? markDummy(`${followersLabel} ${t("People.Followers")}`)
                    : `${followersLabel} ${t("People.Followers")}`}
                </h1>
              ) : (
                <h1>
                  {mutualIsDummy
                    ? markDummy(
                        type === "Company"
                          ? t("SearchJob.Network follows company")
                          : t("User Profile.Mutual Connections")
                      )
                    : mutual}
                </h1>
              )}
            </div>
          ) : (
            <div />
          )}
          <div className="follower-button">
            <button
              type="button"
              className={`${
                isFollowed ? "wljDangerBtn" : "wljPrimaryBtn"
              } wlj-chip-btn`}
              onClick={(e) =>
                handleFollowClick(
                  e,
                  type,
                  item?.id,
                  item?.isFollowedByYou,
                  name || displayTitle || item?.displayName || item?.title || ""
                )
              }
            >
              {isFollowed ? (
                <>
                  <PersonRemoveAlt1SharpIcon />
                  {t("Recommendations.Unfollow")}
                </>
              ) : (
                <>
                  <PersonAddAlt1SharpIcon />
                  {t("People.Follow")}
                </>
              )}
            </button>
            {type === "User" && messageLink ? (
              <Link
                to={messageLink}
                className="wljSecondaryBtn wlj-chip-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <ChatBubbleOutlineIcon />
                {t("Notifications.Message")}
              </Link>
            ) : (
              <Link
                to={itemLink}
                className="wljSecondaryBtn wlj-chip-btn"
                onClick={(e) => e.stopPropagation()}
              >
                <VisibilityOutlinedIcon />
                {t("Manage Job.View")}
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderJobCard = (item, index) => {
    const dummy = pickDummy();
    const apiTitle = normalizeSearchTitle(item?.title);
    const designation = normalizeSearchMeta(item?.additionalInfo);
    const apiCompany = item?.compName || item?.companyName || "";
    const parsed = splitJobMeta(item?.description);
    const apiLocation =
      normalizeSearchMeta(item?.location) || parsed.location || "";
    const apiPosted =
      (item?.creationTime || item?.createdOn) &&
      moment(item.creationTime || item.createdOn).isValid()
        ? moment(item.creationTime || item.createdOn).fromNow()
        : "";
    const apiJobType = item?.jbTypName || item?.jobType || "";
    const apiWorkType =
      item?.workLocationTypeName || item?.workType || "";

    const titleIsDummy = !apiTitle;
    const companyIsDummy = !apiCompany;
    const locationIsDummy = !apiLocation;
    const postedIsDummy = !apiPosted;
    const jobTypeIsDummy = !apiJobType;
    const workTypeIsDummy = !apiWorkType;

    const jobTitle = apiTitle || "Open Position";
    const companyName = apiCompany || dummy.company;
    const jobLocation = apiLocation || dummy.location;
    const posted = apiPosted || dummy.posted;
    const jobType = apiJobType || dummy.jobType;
    const workType = apiWorkType || dummy.workType;
    const jobId = item?.jobId || item?.id;
    const itemLink = `/JobDetail/${jobId}/${item?.slug || ""}`;
    const stateKey = `Job-${jobId}`;
    const isSaved = Object.prototype.hasOwnProperty.call(savedMap, stateKey)
      ? savedMap[stateKey]
      : Boolean(item?.isSaved);
    const isApplied = Object.prototype.hasOwnProperty.call(appliedMap, stateKey)
      ? appliedMap[stateKey]
      : Boolean(item?.isApplied);
    const expiryDays = Number(item?.expiringInDays);
    const hasExpiryDays = Number.isFinite(expiryDays);
    const isApplyDisabled =
      Boolean(item?.isExpired) || Boolean(item?.isClosed) || Boolean(item?.isOwned);

    return (
      <article
        key={`Job-${item?.id || index}`}
        className="jobCard p-3 w-100 d-flex flex-column justify-content-between sr-job-result"
      >
        <div>
          <div className="d-flex justify-content-start align-items-center job-company-row">
            <img
              className="job-company-logo"
              src={item?.image || people.DefaultProfile}
              alt=""
              onError={handleImageError}
            />
            <span className="job-company-name">
              {companyIsDummy ? markDummy(companyName) : companyName}
            </span>
            <button
              type="button"
              className="sr-job-bookmark ms-auto"
              aria-label={isSaved ? t("Newsfeed.Saved") : t("Newsfeed.Save")}
              onClick={(event) => handleSaveClick(event, item, "Job")}
            >
              {isSaved ? <BookmarkOutlinedIcon /> : <BookmarkBorderOutlinedIcon />}
            </button>
          </div>

          <Link to={itemLink} className="job-title-link">
            <p className="job-title pointer mb-0">
              {titleIsDummy ? markDummy(jobTitle) : jobTitle}
            </p>
          </Link>
          {designation && designation !== companyName ? (
            <p className="short-Des-jobCard sr-job-designation">{designation}</p>
          ) : null}
          <div className="job-location">
            <LocationOnSharpIcon />
            <span>
              {locationIsDummy ? markDummy(jobLocation) : jobLocation}
            </span>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 job-card-foot">
            <small>
              <ScheduleOutlinedIcon />
              {t("Manage Job.Posted")}{" "}
              {postedIsDummy ? markDummy(posted) : posted}
            </small>
            {hasExpiryDays ? (
              <span className="sr-job-expiry">
                {expiryDays < 0
                  ? `${t("Manage Job.expired")} ${Math.abs(expiryDays)} ${t(
                      "Manage Job.days Ago"
                    )}`
                  : `${expiryDays} ${t("Manage Job.days Left")}`}
              </span>
            ) : null}
          </div>
          <div className="jobType">
            {jobType ? (
              <span className="badge badge-job badge-job-type">
                {jobTypeIsDummy ? markDummy(jobType) : jobType}
              </span>
            ) : null}
            {workType ? (
              <span className="badge badge-job badge-job-location">
                {workTypeIsDummy ? markDummy(workType) : workType}
              </span>
            ) : null}
          </div>
        </div>
        <div className="mt-auto jobAction d-flex justify-content-between align-items-center">
          <Link to={itemLink} className="mainBtn4">
            <VisibilityOutlinedIcon />
            {t("Manage Job.View")}
          </Link>
          <button
            type="button"
            className={`mainBtn${isApplied ? " appliedBtn" : ""}`}
            disabled={isApplyDisabled}
            onClick={(event) => handleApplyClick(event, item)}
          >
            {item?.isClosed || item?.isExpired ? (
              t("Manage Job.Closed Job")
            ) : isApplied ? (
              <>
                <CheckSharpIcon />
                {t("Manage Job.Applied")}
              </>
            ) : (
              <>
                <SendOutlinedIcon />
                {t("SearchJob.Apply")}
              </>
            )}
          </button>
        </div>
      </article>
    );
  };

  const renderNewsfeedSearchCard = ({
    cardKey,
    isDummy = false,
    profileLink,
    itemLink,
    avatarSrc,
    avatarIsHashtag = false,
    name,
    handle,
    subtitle,
    date,
    badgeLabel,
    onFollow,
    isFollowed = false,
    showFollow = true,
    bodyTitle,
    bodyText,
    likes,
    comments,
    reposts,
    hasApiLikes = false,
    hasApiComments = false,
    hasApiReposts = false,
    isSaved = false,
    onSave,
  }) => {
    const safeProfileLink = isDummy || !profileLink ? "#" : profileLink;
    const safeItemLink = isDummy || !itemLink ? "#" : itemLink;

    return (
      <div
        key={cardKey}
        className={`featured-card user-postOnlyContent p-2 mb-3 sr-newsfeed-card${
          isDummy ? " sr-dummy-link" : ""
        }`}
        role="article"
      >
        {isDummy ? (
          <div className="sr-dummy-badge">{t("SearchJob.Dummy data")} *</div>
        ) : null}

        <div className="feature-content p-md-2 d-flex justify-content-between mb-0 RemovePaddingMobile">
          <Link
            to={safeProfileLink}
            className="mb-1 sr-newsfeed-profile"
            onClick={(event) => {
              if (isDummy || safeProfileLink === "#") event.preventDefault();
            }}
          >
            <div className="d-flex">
              <div className="ImgAvatar">
                {avatarIsHashtag ? (
                  <div className="sr-hashtag-avatar">
                    <TagIcon />
                  </div>
                ) : (
                  <img
                    src={avatarSrc || people.DefaultProfile}
                    alt=""
                    style={{ borderRadius: "50px" }}
                    onError={handleImageError}
                  />
                )}
              </div>
              <div className="ms-2 mb-2">
                <div className="d-flex userName align-items-center gap-1 flex-wrap">
                  <h4 className="f-14 fw-bold mb-0" dir="ltr">
                    {name}
                  </h4>
                  {handle ? (
                    <span
                      className="f-12 fw-600"
                      dir="ltr"
                      style={{ color: "rgb(58, 116, 246)" }}
                    >
                      {handle}
                    </span>
                  ) : null}
                </div>
                {subtitle ? <p className="f-12 mb-0">{subtitle}</p> : null}
                {date ? (
                  <div className="d-flex justify-content-start align-items-center">
                    <p className="f-10 label mb-0">{date}</p>
                    <span className="f-800 me-1 ms-1">·</span>
                    <PublicSharpIcon
                      fontSize="inherit"
                      className="label sr-public-icon"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </Link>

          <div className="d-flex align-items-start sr-newsfeed-header-actions">
            {badgeLabel ? (
              <span className="badge badge-subtle badge-info1 f-12 me-2">
                {badgeLabel}
              </span>
            ) : null}
            {showFollow ? (
              <button
                type="button"
                className={`${
                  isFollowed ? "wljDangerBtn" : "wljPrimaryBtn"
                } wlj-chip-btn sr-follow-inline`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (onFollow) onFollow(event);
                }}
              >
                {isFollowed ? (
                  <>
                    <PersonRemoveAlt1SharpIcon />
                    {t("Recommendations.Unfollow")}
                  </>
                ) : (
                  <>
                    <PersonAddAlt1SharpIcon />
                    {t("People.Follow")}
                  </>
                )}
              </button>
            ) : null}
            <button
              type="button"
              className="sr-more-btn"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              aria-label="More"
            >
              <MoreHorizSharpIcon />
            </button>
          </div>
        </div>

        <div className="sr-newsfeed-body">
          {bodyTitle ? (
            <Link
              to={safeItemLink}
              className="sr-newsfeed-title"
              onClick={(event) => {
                if (isDummy || safeItemLink === "#") event.preventDefault();
              }}
            >
              {bodyTitle}
            </Link>
          ) : null}
          {bodyText ? (
            <Link
              to={safeItemLink}
              className="sr-newsfeed-text"
              onClick={(event) => {
                if (isDummy || safeItemLink === "#") event.preventDefault();
              }}
            >
              {bodyText}
            </Link>
          ) : null}
        </div>

        <div className="d-flex justify-content-between align-items-center pt-1 px-2 postengagement sr-newsfeed-actions">
          <span className="d-flex align-items-center postAction">
            <ThumbUpOffAltIcon />
            <span className="f-12 ms-1">
              {hasApiLikes ? likes : likes ? markDummy(String(likes)) : null}
            </span>
            <span className="f-12 ms-1">{t("Newsfeed.Like")}</span>
          </span>
          <span className="d-flex align-items-center postAction">
            <ChatBubbleOutlineIcon />
            <span className="f-12 ms-1">
              {hasApiComments
                ? comments
                : comments
                  ? markDummy(String(comments))
                  : null}
            </span>
            <span className="f-12 ms-1">{t("Newsfeed.Comment")}</span>
          </span>
          <span className="d-flex align-items-center postAction">
            <ReplyAllOutlinedIcon />
            <span className="f-12 ms-1">
              {hasApiReposts
                ? reposts
                : reposts
                  ? markDummy(String(reposts))
                  : null}
            </span>
          </span>
          <span className="d-flex align-items-center postAction">
            <ShareOutlinedIcon />
            <span className="f-12 ms-1">{t("Newsfeed.Share")}</span>
          </span>
          <button
            type="button"
            className="d-flex align-items-center postAction sr-footer-action"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (onSave) onSave(event);
            }}
          >
            {isSaved ? <BookmarkOutlinedIcon /> : <BookmarkBorderOutlinedIcon />}
            <span className="f-12 ms-1">{t("Newsfeed.Save")}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderPostLikeCard = (item, type, index) => {
    if (type === "Article") {
      return renderArticleCard(item, index);
    }

    if (type === "Hashtag") {
      return renderHashtagCard(item, index);
    }

    return renderFeedPostCard(item, index);
  };

  const renderHashtagCard = (item, index) => {
    const formatted = formatPostSearchItem(item);
    const dummy = pickDummy();
    const apiTitle = normalizeSearchTitle(item?.title);
    const authorIsDummy = !apiTitle;
    const authorName = apiTitle || (item?.slug ? `#${item.slug}` : "");
    const apiContent = formatted.description;
    const contentIsDummy = !apiContent;
    const contentText = apiContent;
    const itemLink = `/HashTag/${encodeURIComponent(
      item?.slug || formatted.title || authorName
    )}`;
    const displayName = authorIsDummy
      ? markDummy(
          authorName.startsWith("#") ? authorName : `#${authorName}`
        )
      : authorName.startsWith("#")
        ? authorName
        : `#${authorName}`;
    const followKey = `Hashtag-${item?.id || index}`;
    const isFollowed = Boolean(followedMap[followKey]);

    return renderNewsfeedSearchCard({
      cardKey: `Hashtag-${item?.id || index}`,
      profileLink: itemLink,
      itemLink,
      avatarIsHashtag: true,
      name: displayName,
      subtitle: contentIsDummy
        ? markDummy("Popular hashtag conversations")
        : contentText
          ? truncateText(contentText, 90)
          : t("Newsfeed.Hashtag"),
      badgeLabel: t("Newsfeed.Hashtag"),
      isFollowed,
      onFollow: () => {
        setFollowedMap((prev) => ({
          ...prev,
          [followKey]: !prev[followKey],
        }));
      },
      bodyText: contentIsDummy
        ? markDummy(truncateText(contentText || dummy.content, 220))
        : contentText
          ? truncateText(contentText, 220)
          : null,
      onSave: (event) => handleSaveClick(event, item, "Hashtag"),
    });
  };

  const renderFeedPostCard = (item, index) => {
    const isFullDummy = Boolean(item?.isDummy);
    const formatted = formatPostSearchItem(item);
    const dummy = pickDummy();
    const itemLink = isFullDummy ? "#" : getPostSearchLink(item);

    const apiAuthor =
      (typeof item?.author === "object" ? item?.author?.name : item?.author) ||
      parseTitleAndHandle(formatted.additionalInfo || "").name ||
      normalizeSearchMeta(item?.additionalInfo) ||
      "";

    const rawTitle = formatted.title || normalizeSearchTitle(item?.title);
    const rawDescription = formatted.description || normalizeSearchMeta(item?.description);
    const enrichedContent = stripHtmlToText(
      item?.content || item?.shortDescription || ""
    );

    // Global search often returns a date in title; prefer enriched post body
    let apiContent = enrichedContent;
    if (!apiContent) {
      if (rawTitle && !looksLikeDateOnly(rawTitle)) apiContent = rawTitle;
      else if (rawDescription && !looksLikeDateOnly(rawDescription)) {
        apiContent = rawDescription;
      }
    }

    const apiHeadline =
      item?.headline ||
      (rawDescription &&
      rawDescription !== apiContent &&
      !looksLikeDateOnly(rawDescription)
        ? rawDescription
        : "") ||
      "";

    const dateCandidate =
      item?.creationTime ||
      item?.createdOn ||
      item?.date ||
      (looksLikeDateOnly(rawTitle) ? rawTitle : "") ||
      (looksLikeDateOnly(rawDescription) ? rawDescription : "");
    const apiDate =
      dateCandidate && moment(dateCandidate).isValid()
        ? moment(dateCandidate).fromNow()
        : "";

    const authorIsDummy = isFullDummy || !apiAuthor;
    const headlineIsDummy = isFullDummy || !apiHeadline;
    const contentIsDummy = isFullDummy || !apiContent;
    const dateIsDummy = isFullDummy || !apiDate;

    const authorName = apiAuthor || dummy.author;
    const authorTitle = apiHeadline || dummy.title;
    const contentText = apiContent || dummy.content;
    const displayDate = apiDate || dummy.date;

    const hasApiLikes = item?.likeCount != null || item?.likes != null;
    const hasApiComments = item?.commentCount != null || item?.comments != null;
    const hasApiReposts =
      item?.repostCount != null ||
      item?.shareCount != null ||
      item?.reposts != null;
    const likes = hasApiLikes
      ? item?.likeCount ?? item?.likes
      : dummy.likes;
    const comments = hasApiComments
      ? item?.commentCount ?? item?.comments
      : dummy.comments;
    const reposts = hasApiReposts
      ? item?.repostCount ?? item?.shareCount ?? item?.reposts
      : dummy.reposts;

    const followKey = `User-${item?.userUid || item?.id || index}`;
    const isFollowed = Object.prototype.hasOwnProperty.call(
      followedMap,
      followKey
    )
      ? Boolean(followedMap[followKey])
      : Boolean(item?.isFollowedByYou);
    const saveKey = `Post-${item?.id}`;
    const isSaved = Object.prototype.hasOwnProperty.call(savedMap, saveKey)
      ? savedMap[saveKey]
      : Boolean(item?.isSavedByYou);
    const profileLink = item?.userUid
      ? `/User/${item.userUid}`
      : isFullDummy
        ? "#"
        : itemLink;

    return renderNewsfeedSearchCard({
      cardKey: isFullDummy
        ? `Post-dummy-${item?.id || index}`
        : `Post-${item?.id || index}`,
      isDummy: isFullDummy,
      profileLink,
      itemLink,
      avatarSrc:
        item?.authorProfilePic || item?.image || people.DefaultProfile,
      name: authorIsDummy ? markDummy(authorName) : authorName,
      handle: item?.nameTag || "",
      subtitle: authorTitle
        ? headlineIsDummy
          ? markDummy(truncateText(authorTitle, 100))
          : truncateText(authorTitle, 100)
        : "",
      date: displayDate
        ? dateIsDummy
          ? markDummy(displayDate)
          : displayDate
        : "",
      isFollowed,
      onFollow: (event) => {
        if (item?.userUid) {
          handleFollowClick(
            event,
            "User",
            item.userUid,
            item?.isFollowedByYou,
            authorName || item?.additionalInfo || ""
          );
        } else {
          setFollowedMap((prev) => ({
            ...prev,
            [followKey]: !prev[followKey],
          }));
        }
      },
      bodyText: contentIsDummy
        ? markDummy(truncateText(contentText, 280))
        : truncateText(contentText, 280),
      likes,
      comments,
      reposts,
      hasApiLikes,
      hasApiComments,
      hasApiReposts,
      isSaved,
      onSave: (event) => handleSaveClick(event, item, "Post"),
    });
  };

  const renderArticleCard = (item, index) => {
    const isFullDummy = Boolean(item?.isDummy);
    const dummy = pickDummy();
    const apiTitle = normalizeSearchTitle(item?.title);
    const apiAuthor = normalizeSearchMeta(
      typeof item?.author === "object"
        ? item?.author?.name
        : item?.author || item?.additionalInfo
    );
    const apiDescription = normalizeSearchMeta(
      item?.shortDescription || item?.content || item?.description
    );
    const dateCandidate = item?.creationTime || item?.createdOn || item?.date;
    const apiDate =
      dateCandidate && moment(dateCandidate).isValid()
        ? moment(dateCandidate).format("D MMMM YYYY [at] h:mm a")
        : "";

    const title = apiTitle || dummy.title;
    const author = apiAuthor || dummy.author;
    const authorTitle =
      item?.authorTitle ||
      (isFullDummy || !apiAuthor ? dummy.authorTitle : "");
    const description = apiDescription || dummy.description;
    const displayDate = apiDate || dummy.date;

    const titleIsDummy = isFullDummy || !apiTitle;
    const authorIsDummy = isFullDummy || !apiAuthor;
    const descIsDummy = isFullDummy || !apiDescription;
    const dateIsDummy = isFullDummy || !apiDate;

    const followKey = `User-${item?.userUid || item?.id || index}`;
    const isFollowed = Object.prototype.hasOwnProperty.call(
      followedMap,
      followKey
    )
      ? Boolean(followedMap[followKey])
      : Boolean(item?.isFollowedByYou);
    const saveKey = `Article-${item?.id}`;
    const isSaved = Object.prototype.hasOwnProperty.call(savedMap, saveKey)
      ? savedMap[saveKey]
      : Boolean(item?.isSavedByYou);
    const itemLink = `/Articles/${item?.id}/${item?.slug || ""}`;
    const profileLink = item?.userUid ? `/User/${item.userUid}` : itemLink;

    return renderNewsfeedSearchCard({
      cardKey: isFullDummy
        ? `Article-dummy-${item?.id || index}`
        : `Article-${item?.id || index}`,
      isDummy: isFullDummy,
      profileLink,
      itemLink,
      avatarSrc: item?.authorProfilePic || people.DefaultProfile,
      name: authorIsDummy ? markDummy(author) : author,
      subtitle: authorTitle
        ? authorIsDummy
          ? markDummy(authorTitle)
          : authorTitle
        : "",
      date: displayDate
        ? dateIsDummy
          ? markDummy(displayDate)
          : displayDate
        : "",
      badgeLabel: t("Newsfeed.Article"),
      isFollowed,
      showFollow: Boolean(item?.userUid) || isFullDummy,
      onFollow: (event) => {
        if (item?.userUid) {
          handleFollowClick(
            event,
            "User",
            item.userUid,
            item?.isFollowedByYou,
            author || item?.additionalInfo || ""
          );
        } else {
          setFollowedMap((prev) => ({
            ...prev,
            [followKey]: !prev[followKey],
          }));
        }
      },
      bodyTitle: titleIsDummy ? markDummy(title) : title,
      bodyText: descIsDummy
        ? markDummy(truncateText(description, 220))
        : truncateText(description, 220),
      likes: item?.likeCount,
      comments: item?.commentCount,
      hasApiLikes: item?.likeCount != null,
      hasApiComments: item?.commentCount != null,
      isSaved,
      onSave: (event) => handleSaveClick(event, item, "Article"),
    });
  };

  const renderBlogCard = (item, index) => {
    const dummy = pickDummy();
    const apiTitle = normalizeSearchTitle(item?.title);
    const description = normalizeSearchMeta(item?.description);
    const additionalInfo = normalizeSearchMeta(item?.additionalInfo);
    const dateLooksLikeDate = moment(
      item?.createdOn || item?.description
    ).isValid();
    const blogSlug = item?.additionalInfo || item?.slug || item?.id || "";
    const itemLink = `/blogs/${blogSlug}`;

    const titleIsDummy = !apiTitle;
    const title = apiTitle;

    const apiShortDesc = dateLooksLikeDate
      ? ""
      : description && description !== title
        ? description
        : additionalInfo &&
            additionalInfo !== title &&
            additionalInfo !== blogSlug
          ? additionalInfo
          : "";
    const descIsDummy = !apiShortDesc;
    const shortDesc = apiShortDesc || dummy.description;

    const dateIsDummy = !dateLooksLikeDate;
    const blogDate = dateLooksLikeDate
      ? formatBlogDate(item?.createdOn || item?.description)
      : dummy.date;

    return (
      <Link
        to={itemLink}
        key={`Blog-${item?.id || index}`}
        className="search-result-card sr-blog-card"
      >
        {item?.image ? (
          <img
            className="sr-blog-image"
            src={item.image}
            alt=""
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : null}
        <div className="sr-blog-content">
          <div className="sr-blog-title">
            {titleIsDummy ? markDummy(title) : title}
          </div>
          {item?.category ? (
            <p className="sr-blog-desc f-12">{item.category}</p>
          ) : null}
          {shortDesc ? (
            <p className="sr-blog-desc">
              {descIsDummy
                ? markDummy(truncateText(shortDesc, 140))
                : truncateText(shortDesc, 140)}
            </p>
          ) : null}
          {blogDate ? (
            <p className="sr-blog-date">
              {dateIsDummy ? markDummy(blogDate) : blogDate}
            </p>
          ) : null}
        </div>
      </Link>
    );
  };

  const renderItems = (items, type) => {
    if (isLoading && page === 1) {
      return renderEmptyState();
    }

    const displayItems = items || [];

    if (!displayItems.length) {
      return renderEmptyState();
    }

    if (type === "Blog") {
      return (
        <div className="sr-blog-grid">
          {displayItems.map((item, index) => renderBlogCard(item, index))}
        </div>
      );
    }

    const listClass =
      type === "Job"
        ? "search-result-list sr-job-grid"
        : type === "User" || type === "Company"
          ? "search-result-list sr-entity-grid"
          : type === "Post" || type === "Hashtag" || type === "Article"
            ? "search-result-list sr-newsfeed-list"
            : "search-result-list";

    return (
      <div className={listClass}>
        {displayItems.map((item, index) => {
          if (type === "User" || type === "Company") {
            return renderPeopleOrCompanyCard(item, type, index);
          }
          if (type === "Job") {
            return renderJobCard(item, index);
          }
          if (type === "Article") {
            return renderArticleCard(item, index);
          }
          if (type === "Post" || type === "Hashtag") {
            return renderPostLikeCard(item, type, index);
          }
          return null;
        })}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return renderItems(searchUser, "User");
      case 2:
        return renderItems(searchComp, "Company");
      case 3:
        return renderItems(searchJob, "Job");
      case 4:
        return renderItems(searchPost, "Post");
      case 5:
        return renderItems(searchHashtag, "Hashtag");
      case 6:
        return renderItems(searchArticle, "Article");
      case 7:
        return renderItems(searchBlog, "Blog");
      default:
        return renderEmptyState();
    }
  };

  const getActiveItems = () => {
    switch (activeTab) {
      case 1:
        return searchUser;
      case 2:
        return searchComp;
      case 3:
        return searchJob;
      case 4:
        return searchPost;
      case 5:
        return searchHashtag;
      case 6:
        return searchArticle;
      case 7:
        return searchBlog;
      default:
        return [];
    }
  };

  return (
    <>
      <Helmet>
        <title>Search Results | Welinkjobs</title>
        <meta
          name="description"
          content="Browse search results across jobs, companies, posts, people, blogs, and hashtags."
        />
      </Helmet>

      <div className="friend-main-container discovery-page search-result-page">
        <div className="headerSection d-block d-md-flex justify-content-between">
          <Col md={7}>
            <div className="friendsHeaderText">
              <h1>{t("SearchJob.See all results")}</h1>
              <p className="discovery-subtitle">
                {t("SearchJob.DiscoverSubtitle", {
                  defaultValue:
                    "Browse people, companies, jobs, posts, and more.",
                })}
              </p>
            </div>
          </Col>
          <Col md={5}>
            <div className="wlj-search-field">
              <SearchSharpIcon className="wlj-search-field-icon" />
              <input
                type="search"
                className="form-control discovery-search"
                placeholder={t("SearchJob.See all results")}
                onChange={(e) => {
                  setHasTabChanged(false);
                  setPage(1);
                  setInputValue(e.target.value);
                }}
                value={inputValue}
              />
            </div>
          </Col>
        </div>

        <div className="d-flex flex-column">
          <div
            className="tabButton d-flex p-2 w-100 me-2 connectionTab"
            role="tablist"
          >
            {SEARCH_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`text-center border-0 bg-transparent p-0 ${
                    activeTab === tab.id ? "activeTab" : ""
                  }`}
                  onClick={() => goToSearchTab(tab.id)}
                >
                  <Icon />
                  <p className="f-14 tabName">{t(tab.labelKey)}</p>
                </button>
              );
            })}
          </div>

          <div className="tab-content pt-2">
            {renderTabContent()}

            {nextBatchLoading && (
              <div className="text-center">
                <img src={post?.loader} width={50} alt="Loading" />
              </div>
            )}

            {!hasMoreData &&
              !isLoading &&
              getActiveItems().length > 0 &&
              page > 1 && (
                <p className="text-center mt-3">
                  {t("Newsfeed.No more data available")}
                </p>
              )}
          </div>
        </div>
      </div>

      <SuccessToast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
      />
      <WarningToast
        show={showWarningToast}
        message={warningToastMessage}
        onClose={() => setShowWarningToast(false)}
      />
      <ErrorToast
        show={showErrorToast}
        message={errorToastMessage}
        onClose={() => setShowErrorToast(false)}
      />

      {showUnfollowModal && (
        <div
          className="modal fade show postAdd"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content d-flex justify-content-around p-2">
              <div className="modalBorder d-flex justify-content-between align-items-center p-2">
                <h6>
                  {t("modals.Are you sure you want to unfollow")}
                  {unfollowTarget?.name ? (
                    <b> {unfollowTarget.name}</b>
                  ) : null}
                </h6>
                <CloseSharpIcon
                  className="material-symbols-sharp me-2 pointer"
                  onClick={closeUnfollowModal}
                />
              </div>
              <div className="text-center mt-2">
                <button
                  type="button"
                  className="closebtn"
                  onClick={confirmUnfollow}
                >
                  {t("Recommendations.Unfollow")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SearchResult;
